import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { formatBidNumber } from '../utils/bidNumber';
import { parseWallClock } from '../utils/datetime';
import '../styles/tv.css';

const REFRESH_INTERVAL = 60000;
const DAY = 86400000;
const TWO_WEEKS = 14 * DAY;
const SEVEN_DAYS = 7 * DAY;
const ACTIVITY_PAGE = 25;

// A bid is considered submitted (out of the live countdown) once it reaches
// any of these statuses. Pending = "proposal submitted, awaiting response".
const SUBMITTED = ['Pending', 'Awarded', 'Not Awarded'];

// Owner deadline is handled on its own (it's the alarm/throb deadline). These
// are the prep milestones — shown only while upcoming, dropped once they pass.
const OTHER_DEADLINES = [
  { key: 'SubDueDate', label: 'SUB BID DUE', color: '#ef4444' },
];

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function formatCountdown(ms) {
  if (ms <= 0) return 'OVERDUE';
  const d = Math.floor(ms / DAY);
  const h = Math.floor((ms % DAY) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return `${m}m ${s}s`;
}

function formatCurrency(val) {
  if (!val) return '$0';
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

// "due in" short label for a deadline relative to now
function dueLabel(ms) {
  if (ms == null || !isFinite(ms)) return null;
  if (ms <= 0) return 'OVERDUE';
  const d = Math.floor(ms / DAY);
  if (d >= 1) return `${d}d`;
  const h = Math.floor(ms / 3600000);
  return h >= 1 ? `${h}h` : '<1h';
}

export default function TvDashboard() {
  const [projects, setProjects] = useState([]);
  const [activity, setActivity] = useState([]);
  const [aiUsage, setAiUsage] = useState([]);
  const [activityLimit, setActivityLimit] = useState(ACTIVITY_PAGE);
  const now = useClock();
  const navigate = useNavigate();
  const go = (id) => { if (id) navigate(`/projects/${id}`); };

  // Projects refresh on an interval (drives KPIs, countdowns, must-win, wins).
  useEffect(() => {
    const loadP = () => api.getProjects().then(setProjects).catch(() => {});
    loadP();
    const id = setInterval(loadP, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, []);

  // Activity refetches when the lazy-load grows the page, and on the interval.
  useEffect(() => {
    const loadA = () => api.getActivityLog({ limit: activityLimit })
      .then(d => setActivity(Array.isArray(d) ? d : []))
      .catch(() => {});
    loadA();
    const id = setInterval(loadA, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [activityLimit]);

  // AI usage (daily tokens + calls, last 30 days) drives the AI adoption KPI.
  useEffect(() => {
    const loadAI = () => api.getAiUsage()
      .then(d => setAiUsage(Array.isArray(d) ? d : []))
      .catch(() => {});
    loadAI();
    const id = setInterval(loadAI, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, []);

  // Grow the activity page when scrolled near the bottom (stop once a fetch
  // returns fewer rows than asked for — that means we've hit the end).
  const activityRef = useRef(null);
  function onActivityScroll() {
    const el = activityRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80 && activity.length >= activityLimit) {
      setActivityLimit(l => l + ACTIVITY_PAGE);
    }
  }

  // Opportunities (Pipeline status) are excluded from the entire TV — every
  // panel and KPI below derives from `shown`, never the raw projects list.
  const shown = useMemo(() => projects.filter(p => p.StatusName !== 'Pipeline'), [projects]);

  // Department health: wins in rolling 30/60/90-day windows + year-to-date,
  // valued by the actual awarded amount. Win rate is all-time decided.
  const stats = useMemo(() => {
    const t = Date.now();
    const val = (arr) => arr.reduce((s, p) => s + (p.AwardedAmount || p.ProjectCost || 0), 0);
    const awarded = shown.filter(p => p.StatusName === 'Awarded');
    const notAwarded = shown.filter(p => p.StatusName === 'Not Awarded');
    const decided = awarded.length + notAwarded.length;

    const awardedDated = awarded
      .map(p => ({ p, d: p.DateAwarded ? parseWallClock(p.DateAwarded) : null }))
      .filter(x => x.d);
    const within = (days) => {
      const cut = t - days * DAY;
      const w = awardedDated.filter(x => x.d.getTime() >= cut).map(x => x.p);
      return { count: w.length, value: val(w) };
    };
    const yearStart = new Date(new Date().getFullYear(), 0, 1).getTime();
    const ytd = awardedDated.filter(x => x.d.getTime() >= yearStart).map(x => x.p);

    return {
      win30: within(30),
      win60: within(60),
      win90: within(90),
      ytd: { count: ytd.length, value: val(ytd) },
      winRate: decided > 0 ? Math.round((awarded.length / decided) * 100) : 0,
      winFraction: `${awarded.length}/${decided}`,
      total: shown.length,
    };
  }, [shown]);

  // AI adoption: total calls (last 30d) + up/down trend (last 15 days vs the
  // prior 15) + a daily sparkline. Built as a CONTINUOUS 30-day series with
  // zero-filled gaps, so the sparkline reflects the real timeline (idle days
  // dip to zero) and the 15/15 trend split is even by day.
  const ai = useMemo(() => {
    const rows = Array.isArray(aiUsage) ? aiUsage : [];
    const byDate = {};
    rows.forEach(r => { byDate[r.date] = (byDate[r.date] || 0) + (r.calls || 0); });
    const today = new Date();
    const series = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      series.push(byDate[key] || 0);
    }
    const totalCalls = series.reduce((s, c) => s + c, 0);
    const last15 = series.slice(15).reduce((s, c) => s + c, 0);
    const prior15 = series.slice(0, 15).reduce((s, c) => s + c, 0);
    let trendPct = null;
    if (prior15 > 0) trendPct = Math.round(((last15 - prior15) / prior15) * 100);
    else if (last15 > 0) trendPct = 100;
    return { totalCalls, last15, prior15, trendPct, spark: series };
  }, [aiUsage]);

  // Monthly win-rate trend (last 6 months) for the sparkline. Decided date is
  // DateAwarded for wins, UpdatedAt otherwise.
  const winTrend = useMemo(() => {
    const months = {};
    shown.forEach(p => {
      if (p.StatusName !== 'Awarded' && p.StatusName !== 'Not Awarded') return;
      const raw = p.StatusName === 'Awarded' ? (p.DateAwarded || p.UpdatedAt) : p.UpdatedAt;
      if (!raw) return;
      const d = new Date(raw);
      if (isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months[key] = months[key] || { w: 0, l: 0 };
      if (p.StatusName === 'Awarded') months[key].w++; else months[key].l++;
    });
    return Object.keys(months).sort().slice(-6).map(k => {
      const m = months[k]; const tot = m.w + m.l;
      return tot ? Math.round((m.w / tot) * 100) : 0;
    });
  }, [shown]);

  // Live countdowns. Owner deadline = the alarm (throbs red when missed &
  // unsubmitted, within a 2-week cap; turns into a green "submitted on time"
  // win for ~24h once submitted). Other deadlines just count down and drop off.
  const countdowns = useMemo(() => {
    const items = [];
    const t = Date.now();
    shown.forEach(p => {
      const submitted = SUBMITTED.includes(p.StatusName);
      if (p.GCOwnerDeadline) {
        const owner = parseWallClock(p.GCOwnerDeadline);
        if (owner) {
          if (submitted) {
            const sub = p.DateBidSubmitted ? parseWallClock(p.DateBidSubmitted) : null;
            if (sub && sub <= owner && (t - sub.getTime()) <= DAY) {
              items.push({ projectId: p.ProjectId, bid: p.BidNumber, rev: p.RevisionNumber, name: p.ProjectName, type: 'OWNER', kind: 'submitted', sortMs: -Infinity });
            }
          } else {
            const ms = owner.getTime() - t;
            if (ms >= -TWO_WEEKS && ms <= SEVEN_DAYS) {
              items.push({ projectId: p.ProjectId, bid: p.BidNumber, rev: p.RevisionNumber, name: p.ProjectName, type: 'OWNER DEADLINE', color: '#f59e0b', kind: 'owner', deadline: owner, sortMs: ms });
            }
          }
        }
      }
      if (!submitted) {
        OTHER_DEADLINES.forEach(dt => {
          if (!p[dt.key]) return;
          const d = parseWallClock(p[dt.key]);
          if (!d) return;
          const ms = d.getTime() - t;
          if (ms > 0 && ms <= SEVEN_DAYS) {
            items.push({ projectId: p.ProjectId, bid: p.BidNumber, rev: p.RevisionNumber, name: p.ProjectName, type: dt.label, color: dt.color, kind: 'other', deadline: d, sortMs: ms });
          }
        });
      }
    });
    items.sort((a, b) => a.sortMs - b.sortMs);
    return items;
  }, [shown]);

  // Must Win = priority bids still in play, soonest owner deadline first.
  const mustWin = useMemo(() => {
    const t = Date.now();
    return shown
      .filter(p => p.IsPriority && !SUBMITTED.includes(p.StatusName))
      .map(p => {
        const owner = p.GCOwnerDeadline ? parseWallClock(p.GCOwnerDeadline) : null;
        return { projectId: p.ProjectId, name: p.ProjectName, bid: p.BidNumber, rev: p.RevisionNumber, status: p.StatusName, ownerMs: owner ? owner.getTime() - t : Infinity };
      })
      .sort((a, b) => a.ownerMs - b.ownerMs)
      .slice(0, 8);
  }, [shown]);

  // Recent Wins = projects Awarded in the last 90 days, newest first. Each chip
  // shows the win date (DateAwarded — the date it was switched to Awarded).
  const recentWins = useMemo(() => {
    const t = Date.now();
    return shown
      .filter(p => p.StatusName === 'Awarded' && p.DateAwarded)
      .map(p => ({ p, d: parseWallClock(p.DateAwarded) }))
      .filter(x => x.d && (t - x.d.getTime()) <= 90 * DAY)
      .sort((a, b) => b.d.getTime() - a.d.getTime())
      .slice(0, 24)
      .map(({ p, d }) => ({
        projectId: p.ProjectId, name: p.ProjectName, bid: p.BidNumber, rev: p.RevisionNumber,
        amount: p.AwardedAmount || p.ProjectCost || 0,
        estimator: p.Estimator, coordinator: p.BidCoordinator,
        date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      }));
  }, [shown]);

  const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  // One win chip. Key is passed in because the ticker renders the list twice.
  const renderWin = (w, key) => (
    <span key={key} className="tv-win-chip tv-clickable" onClick={() => go(w.projectId)} role="button" tabIndex={0}>
      <span className="tv-win-top">
        <span className="tv-win-name">{w.name}</span>
        <span className="tv-win-amount">{formatCurrency(w.amount)}</span>
      </span>
      <span className="tv-win-people">
        <span className="tv-win-date">{w.date}</span>
        <span className="tv-win-role"> EST</span> {w.estimator || 'Unassigned'}
        <span className="tv-win-role"> &middot; EC</span> {w.coordinator || 'Unassigned'}
      </span>
    </span>
  );

  // Animate as a marquee once there are enough wins to fill the row; a handful
  // just sit in a static strip (a short loop would leave an obvious gap/jump).
  const tickerWins = recentWins.length > 4;

  return (
    <div className="tv-root">
      <div className="tv-header">
        <div className="tv-header-left">
          <img src="/cm-corp-logo.png" alt="CM Corp" className="tv-logo" />
          <span className="tv-title">WIN Board</span>
        </div>
        <div className="tv-header-right">
          <div className="tv-clock">{timeStr}</div>
          <div className="tv-date">{dateStr}</div>
        </div>
      </div>

      <div className="tv-kpi-row">
        <div className="tv-kpi tv-kpi-green">
          <div className="tv-kpi-value">{formatCurrency(stats.win30.value)}</div>
          <div className="tv-kpi-label">30-Day Wins</div>
          <div className="tv-kpi-sub">{stats.win30.count} won</div>
        </div>
        <div className="tv-kpi tv-kpi-green">
          <div className="tv-kpi-value">{formatCurrency(stats.win60.value)}</div>
          <div className="tv-kpi-label">60-Day Wins</div>
          <div className="tv-kpi-sub">{stats.win60.count} won</div>
        </div>
        <div className="tv-kpi tv-kpi-green">
          <div className="tv-kpi-value">{formatCurrency(stats.win90.value)}</div>
          <div className="tv-kpi-label">90-Day Wins</div>
          <div className="tv-kpi-sub">{stats.win90.count} won</div>
        </div>
        <div className="tv-kpi tv-kpi-win">
          <div className="tv-kpi-value">{stats.winRate}%</div>
          <div className="tv-kpi-label">Win Rate</div>
          {winTrend.length > 1 ? (
            <svg className="tv-kpi-spark" viewBox={`0 0 ${(winTrend.length - 1) * 10} 20`} preserveAspectRatio="none">
              <polyline
                points={winTrend.map((v, i) => `${i * 10},${20 - (v / 100) * 18}`).join(' ')}
                fill="none" stroke="#22c55e" strokeWidth="1.5" vectorEffect="non-scaling-stroke"
              />
            </svg>
          ) : <div className="tv-kpi-sub">{stats.winFraction}</div>}
        </div>
        <div className="tv-kpi tv-kpi-ai">
          <div className="tv-kpi-value">{ai.totalCalls.toLocaleString()}</div>
          <div className="tv-kpi-label">AI Usage</div>
          {ai.spark.length > 1 && (() => {
            const m = Math.max(1, ...ai.spark);
            return (
              <svg className="tv-kpi-spark" viewBox={`0 0 ${(ai.spark.length - 1) * 10} 20`} preserveAspectRatio="none">
                <polyline
                  points={ai.spark.map((v, i) => `${i * 10},${20 - (v / m) * 18}`).join(' ')}
                  fill="none" stroke="#a855f7" strokeWidth="1.5" vectorEffect="non-scaling-stroke"
                />
              </svg>
            );
          })()}
          {ai.trendPct != null
            ? <div className="tv-kpi-sub" style={{ color: ai.trendPct >= 0 ? '#22c55e' : '#ef4444' }}>
                {ai.trendPct >= 0 ? '▲' : '▼'} {Math.abs(ai.trendPct)}% vs prior 15d
              </div>
            : <div className="tv-kpi-sub">calls · 30d</div>}
        </div>
        <div className="tv-kpi tv-kpi-gold">
          <div className="tv-kpi-value">{formatCurrency(stats.ytd.value)}</div>
          <div className="tv-kpi-label">Awarded YTD</div>
          <div className="tv-kpi-sub">{stats.ytd.count} won</div>
        </div>
      </div>

      <div className="tv-wins">
        <span className="tv-wins-label">🏆 RECENT WINS</span>
        {recentWins.length === 0 ? (
          <span className="tv-wins-empty">No wins in the last 90 days — go get one.</span>
        ) : tickerWins ? (
          <div className="tv-wins-ticker">
            <div className="tv-wins-track" style={{ animationDuration: `${Math.max(40, recentWins.length * 9)}s` }}>
              {recentWins.map((w, i) => renderWin(w, `a${i}`))}
              {recentWins.map((w, i) => renderWin(w, `b${i}`))}
            </div>
          </div>
        ) : (
          <div className="tv-wins-strip">
            {recentWins.map((w, i) => renderWin(w, i))}
          </div>
        )}
      </div>

      <div className="tv-main">
        <div className="tv-panel tv-mustwin">
          <div className="tv-section-title">★ MUST WIN</div>
          {mustWin.length === 0 && <div className="tv-empty">No active must-win bids</div>}
          {mustWin.map((m, i) => {
            const due = dueLabel(m.ownerMs);
            return (
              <div key={i} className="tv-row tv-clickable" onClick={() => go(m.projectId)} role="button" tabIndex={0}>
                <div className="tv-row-main">
                  <div className="tv-row-name">{m.name}</div>
                  <div className="tv-row-bid">{formatBidNumber(m.bid, m.rev)}</div>
                </div>
                <div className={`tv-row-tag ${due === 'OVERDUE' ? 'tv-tag-red' : ''}`}>{due || m.status}</div>
              </div>
            );
          })}
        </div>

        <div className="tv-panel tv-countdowns">
          <div className="tv-section-title">⏱ LIVE COUNTDOWNS</div>
          {countdowns.length === 0 && <div className="tv-empty">No upcoming deadlines</div>}
          {countdowns.map((c, i) => {
            if (c.kind === 'submitted') {
              return (
                <div key={i} className="tv-countdown-item tv-submitted tv-clickable" onClick={() => go(c.projectId)} role="button" tabIndex={0}>
                  <div className="tv-countdown-timer tv-submitted-label">✓ ON TIME</div>
                  <div className="tv-countdown-info">
                    <div className="tv-countdown-type tv-submitted-type">SUBMITTED</div>
                    <div className="tv-countdown-project">{c.name}</div>
                    <div className="tv-countdown-bid">{formatBidNumber(c.bid, c.rev)}</div>
                  </div>
                </div>
              );
            }
            const ms = c.deadline - now;
            const isOverdue = c.kind === 'owner' && ms <= 0;
            const isUrgent = !isOverdue && ms > 0 && ms < DAY;
            return (
              <div key={i} className={`tv-countdown-item tv-clickable ${isOverdue ? 'tv-overdue' : isUrgent ? 'tv-urgent' : ''}`} onClick={() => go(c.projectId)} role="button" tabIndex={0}>
                <div className="tv-countdown-timer">{formatCountdown(ms)}</div>
                <div className="tv-countdown-info">
                  <div className="tv-countdown-type" style={{ color: c.color }}>{c.type}</div>
                  <div className="tv-countdown-project">{c.name}</div>
                  <div className="tv-countdown-bid">{formatBidNumber(c.bid, c.rev)}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="tv-panel tv-activity" ref={activityRef} onScroll={onActivityScroll}>
          <div className="tv-section-title">📋 RECENT ACTIVITY</div>
          {activity.length === 0 && <div className="tv-empty">No recent activity</div>}
          {activity.map((a, i) => (
            <div key={a.ActivityId || i} className={`tv-activity-item ${a.ProjectId ? 'tv-clickable' : ''}`} onClick={() => go(a.ProjectId)} role={a.ProjectId ? 'button' : undefined} tabIndex={a.ProjectId ? 0 : undefined}>
              <div className="tv-activity-time">{formatTime(a.CreatedAt)}</div>
              <div className="tv-activity-desc">
                <span className="tv-activity-who">{a.CreatedBy || 'System'}</span>
                {' '}{a.Description}
                <span className="tv-activity-project"> — {formatBidNumber(a.BidNumber, a.RevisionNumber)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="tv-footer">
        <span>CM Corp &middot; Preconstruction Department</span>
        <span>{stats.total} Projects Tracked</span>
      </div>
    </div>
  );
}
